import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import type { StatusTone } from "@/lib/status-colors"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures, ReportList, ReportRow, ReportSection } from "@/features/staff/reports/report-block"
import { tReports, tSharedReports } from "@/features/staff/reports/format"
import type {
  ActorBucket,
  ActorTotal,
  ControlCancellations,
  ControlReport,
} from "@/features/staff/reports/types"

/**
 * R3 — every way money left without a sale being made, each row carrying who
 * authorized it and why.
 *
 * This is the block the epic is actually for. Every competitor shows how much
 * was discounted; the point here is showing immutably WHO signed for it, which
 * is why the actor list sits beside the reason list in all five sub-blocks
 * rather than being a drill-down.
 *
 * **Nothing here is subtracted from the sales total above.** Sales are already
 * net of discounts — `Location::Order#line_total` is gross minus discount, and
 * the payment subtotal R1 sums is built from it — and no Z report subtracts a
 * refund either. Deducting either figure on this screen would remove the same
 * giveaway twice. This card NAMES that money. Read the query header before
 * "fixing" it.
 *
 * A sub-block with nothing in it does not render, following the till report's
 * rule: an empty "Descuentos $0.00" is a decorative dead control on a screen
 * read at speed. A card where ALL FIVE are empty says so in one line, because
 * there the absence is the finding — a clean control report is the good night.
 */

/**
 * `unattributed` is a finding, not a gap.
 *
 * Every writer in the app stamps an actor, and `system` already covers the one
 * case where none is correct. So a row that reached this bucket means a human
 * chose something and the app failed to record who — the report doing its job.
 * It gets `warning`; `system` gets `disabled`, because housekeeping is nobody's
 * to answer for.
 */
const ACTOR_TONE: Record<ActorBucket, StatusTone> = {
  person: "available",
  system: "disabled",
  unattributed: "warning",
}

/** The actor's own name when there is one; a server-resolved bucket label otherwise. */
function ActorList({ actors, testId }: { actors: ActorTotal[]; testId: string }) {
  if (actors.length === 0) return null

  return (
    <ReportList testId={testId}>
      {actors.map((actor) => (
        <ReportRow
          key={`${actor.bucket}-${actor.actor ?? "none"}`}
          testId={`${testId}-${actor.bucket}`}
          tone={ACTOR_TONE[actor.bucket]}
          label={actor.label}
          meta={tReports("events_count", { count: actor.count })}
          amount={actor.amount === null ? null : formatMoney(actor.amount)}
        />
      ))}
    </ReportList>
  )
}

/**
 * Bill cancellations and line cancellations, which are the same shape and are
 * two blocks on purpose: a voided bill and a struck line are different
 * operational events, and only splitting them keeps the menu value from being
 * counted twice.
 *
 * The system row is the trap this block exists to defuse.
 * `Location::BillClosureService` sweeps still-pending orders off a closing bill
 * with no actor, deliberately — `bill_closed_pending` is not a reason any human
 * can pick. Folded into the staff rows, a busy night reads as staff cancelling
 * food all evening, which is the precise misreading the reason was invented to
 * prevent. So it is named, and it sorts last.
 */
function CancellationsSection({
  block,
  titleKey,
  testId,
}: {
  block: ControlCancellations
  titleKey: string
  testId: string
}) {
  if (block.count === 0) return null

  return (
    <ReportSection title={tReports(titleKey)} testId={testId}>
      <ReportFigures>
        <TillFigure
          label={tReports("cancellations_value_label")}
          value={formatMoney(block.total)}
          hint={tReports("cancellations_value_hint")}
          testId={`${testId}-total`}
        />
        <TillFigure
          label={tReports("cancellations_count_label")}
          value={String(block.count)}
          testId={`${testId}-count`}
        />
        {block.systemCount > 0 ? (
          <TillFigure
            label={tReports("cancellations_system_label")}
            value={String(block.systemCount)}
            hint={tReports("cancellations_system_hint")}
            testId={`${testId}-system`}
          />
        ) : null}
      </ReportFigures>

      <ReportList testId={`${testId}-groups`}>
        {block.groups.map((group) => (
          <ReportRow
            key={`${group.reason ?? "unspecified"}-${String(group.system)}`}
            tone={group.system ? "disabled" : undefined}
            label={group.reasonLabel}
            meta={tReports("events_count", { count: group.count })}
            amount={formatMoney(group.amount)}
          />
        ))}
      </ReportList>

      <ActorList actors={block.actors} testId={`${testId}-actors`} />
    </ReportSection>
  )
}

export function ReportControlBlock({ control }: { control: ControlReport }) {
  const { discounts, billCancellations, orderCancellations, refunds, reopenings } = control

  const empty =
    discounts.count === 0 &&
    billCancellations.count === 0 &&
    orderCancellations.count === 0 &&
    refunds.count === 0 &&
    reopenings.count === 0

  return (
    <Card density="compact" data-testid="report-control">
      <CardHeader className="border-b">
        <CardTitle>{tReports("control_title")}</CardTitle>
        <CardDescription>{tReports("control_scope")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Unconditional, and it is the sentence this card can least afford to
            lose: every figure below names money that is either already inside
            the sales total (discounts) or deliberately outside it (refunds).
            Subtracting any of it is the double count. */}
        <p className="text-xs text-muted-foreground" data-testid="report-control-not-deducted">
          {tSharedReports("notes.control_not_deducted")}
        </p>

        {empty ? (
          <p className="text-sm text-muted-foreground" data-testid="report-control-clean">
            {tReports("control_clean")}
          </p>
        ) : null}

        {/* "Cortesía" is defined by `countsAsRevenue`, the frozen column the Z
            already partitions on, and `kind` rides along as a secondary label.
            The two axes are allowed to disagree — nothing forbids a courtesy
            with a marketing reason — and picking the column the signed close
            used is what keeps the two screens from reporting different totals
            for the same word. */}
        {discounts.count > 0 ? (
          <ReportSection title={tReports("discounts_title")} testId="report-control-discounts">
            <ReportFigures>
              <TillFigure
                label={tReports("discounts_revenue_label")}
                value={formatMoney(discounts.revenueTotal)}
                testId="report-control-discounts-revenue"
              />
              <TillFigure
                label={tReports("discounts_non_revenue_label")}
                value={formatMoney(discounts.nonRevenueTotal)}
                hint={tReports("discounts_non_revenue_hint")}
                testId="report-control-discounts-non-revenue"
              />
              <TillFigure
                label={tReports("discounts_count_label")}
                value={String(discounts.count)}
                testId="report-control-discounts-count"
              />
            </ReportFigures>

            <ReportList testId="report-control-discounts-groups">
              {discounts.groups.map((group) => (
                <ReportRow
                  key={`${group.kind}-${group.reason}-${String(group.countsAsRevenue)}`}
                  label={group.reasonLabel}
                  sublabel={group.kindLabel}
                  meta={tReports("events_count", { count: group.count })}
                  amount={formatMoney(group.amount)}
                />
              ))}
            </ReportList>

            <ActorList actors={discounts.actors} testId="report-control-discounts-actors" />
          </ReportSection>
        ) : null}

        <CancellationsSection
          block={billCancellations}
          titleKey="bill_cancellations_title"
          testId="report-control-bill-cancellations"
        />
        <CancellationsSection
          block={orderCancellations}
          titleKey="order_cancellations_title"
          testId="report-control-order-cancellations"
        />

        {refunds.count > 0 ? (
          <ReportSection title={tReports("refunds_title")} testId="report-control-refunds">
            <ReportFigures>
              <TillFigure
                label={tReports("refunds_drawer_label")}
                value={formatMoney(refunds.drawerTotal)}
                testId="report-control-refunds-drawer"
              />
              <TillFigure
                label={tReports("refunds_provider_label")}
                value={formatMoney(refunds.providerTotal)}
                testId="report-control-refunds-provider"
              />
              <TillFigure
                label={tReports("refunds_count_label")}
                value={String(refunds.count)}
                testId="report-control-refunds-count"
              />
            </ReportFigures>

            <ReportList testId="report-control-refunds-groups">
              {refunds.groups.map((group) => (
                <ReportRow
                  key={`${group.rail}-${group.reason ?? "unspecified"}`}
                  label={group.reasonLabel}
                  sublabel={tReports(`refund_rail_${group.rail}`)}
                  meta={tReports("events_count", { count: group.count })}
                  amount={formatMoney(group.amount)}
                />
              ))}
            </ReportList>

            <ActorList actors={refunds.actors} testId="report-control-refunds-actors" />
          </ReportSection>
        ) : null}

        {/* Reopenings are counts, and there is no peso column at all.
            A reopen moves no money by itself; it removes the lock that stops
            money moving. The bill's total at the moment of the reopen is not
            stored, so any amount here would be either invented or a different
            question — and a zero would read as "nothing was at stake", the
            opposite of why this block exists. It is last because it is the one
            that hides the other four: close the bill, let the drawer be
            counted, reopen it and re-ring the table. */}
        {reopenings.count > 0 ? (
          <ReportSection
            title={tReports("reopenings_title")}
            testId="report-control-reopenings"
            hint={tReports("reopenings_hint")}
          >
            <ReportList testId="report-control-reopenings-groups">
              {reopenings.groups.map((group) => (
                <ReportRow
                  key={group.reason ?? "unspecified"}
                  label={group.reasonLabel}
                  meta={tReports("events_count", { count: group.count })}
                />
              ))}
            </ReportList>

            <ActorList actors={reopenings.actors} testId="report-control-reopenings-actors" />
          </ReportSection>
        ) : null}
      </CardContent>
    </Card>
  )
}
